import { indexOf } from 'lodash';
import { ProductSelection } from '../../constants/constant';
import { IBulkInputsTableData } from '../types/bulk-inputs-table-data.model';
import { IGridToggleData } from '../types/grid-x-to-y-response.model';
import { IPlannerState } from '../types/planner-state.model';
import { IOpportunity } from '../types/ppg-sku-drawer.model';
import { isOpportunitySelectionChanged } from './opportunity-utils';

/**
 * A utility function to transform grid data to a nested array structure for table
 * @param gridMatrix Grid layout data
 * @returns An array of rows for table with item name, and set of opportunities for each row
 */
export default function transformGridDataToTableView(
  gridMatrix: IGridToggleData[][],
  view: ProductSelection
) {
  let itemIndex = 0;
  return gridMatrix
    .map((row) =>
      row
        .map<IBulkInputsTableData[]>((cell) =>
          view === ProductSelection.sku
            ? cell.sku_toggle
                .map((skuGroupedByPPG) => ({
                  skus: skuGroupedByPPG.skus,
                  parentId: skuGroupedByPPG.ppg_id
                }))
                .reduce<IBulkInputsTableData[]>(
                  (prev, curr) => [
                    ...prev,
                    ...curr.skus
                      // Filtering auto generated SKUs
                      .filter((sku) => !(sku.sku_id < 0))
                      .map<IBulkInputsTableData>((sku) => ({
                        itemIndex: itemIndex++,
                        itemName: sku.name,
                        itemId: sku.sku_id,
                        opportunities: sku.opportunity.slice(),
                        comments: sku.comments,
                        parentItemId: curr.parentId,
                        subRows: [],
                        originalItem: sku
                      }))
                  ],
                  []
                )
            : cell.sku_toggle.map<IBulkInputsTableData>((skuGroupedByPPG) => ({
                itemIndex: itemIndex++,
                itemName: skuGroupedByPPG.name,
                itemId: skuGroupedByPPG.ppg_id,
                opportunities: skuGroupedByPPG.opportunity.slice(),
                parentItemId: null,
                comments: skuGroupedByPPG.comments,
                originalItem: skuGroupedByPPG,
                subRows: skuGroupedByPPG.skus
                  // Filtering auto generated SKUs
                  .filter((sku) => !(sku.sku_id < 0))
                  .map<IBulkInputsTableData>((sku, skuIndex) => ({
                    itemIndex: skuIndex,
                    itemName: sku.name,
                    itemId: sku.sku_id,
                    comments: sku.comments,
                    opportunities: sku.opportunity.slice(),
                    parentItemId: skuGroupedByPPG.ppg_id,
                    subRows: [],
                    originalItem: sku
                  }))
              }))
        )
        .flat(1)
    )
    .flat(1);
}

export function getNewOpportunitiesForRow(
  opportunities: number[],
  changedOpportunity: IOpportunity,
  isOpportunityRemoved: boolean
) {
  const newOpportunities = [...opportunities, changedOpportunity.pk_id].filter(
    (opp, index, self) => self.indexOf(opp) === index
  );
  if (isOpportunityRemoved) {
    return newOpportunities.filter((opp) => opp !== changedOpportunity.pk_id);
  }
  return newOpportunities;
}

export function setRowItemOpportunity(
  state: IPlannerState,
  arg: {
    parentIndex: number;
    childIndex: number;
    newOpportunities: number[];
    changedOpportunity: IOpportunity;
  }
) {
  const { childIndex, parentIndex, newOpportunities, changedOpportunity } = arg;
  const isOpportunityChecked =
    newOpportunities.indexOf(changedOpportunity.pk_id) > -1;
  const ppgLevelOpportunities = state.summaryTableOpportunities.ppg.slice();
  const skuLevelOpportunities = state.summaryTableOpportunities.sku.slice();
  if (childIndex === null) {
    if (isOpportunityChecked) {
      state.bulkInputsMetadata.rows[parentIndex].opportunities =
        newOpportunities
          .filter(
            (opportunityId) =>
              !changedOpportunity.disables_opportunity.includes(opportunityId)
          )
          .filter((value, index, self) => self.indexOf(value) === index);
      state.bulkInputsMetadata.rows[parentIndex].subRows.forEach((child) => {
        if (!child.opportunities.includes(changedOpportunity.pk_id)) {
          child.opportunities.push(changedOpportunity.pk_id);
        }
        /**
         * Removing any of the opportunities in child that should be removed on check of current opportunity
         */
        // eslint-disable-next-line no-param-reassign
        child.opportunities = child.opportunities.filter(
          (opportunityId) =>
            !changedOpportunity.disables_opportunity.includes(opportunityId)
        );
      });
    } else {
      state.bulkInputsMetadata.rows[parentIndex].opportunities =
        newOpportunities;
      state.bulkInputsMetadata.rows[parentIndex].subRows.forEach((child) => {
        if (child.opportunities.includes(changedOpportunity.pk_id)) {
          child.opportunities.splice(
            child.opportunities.indexOf(changedOpportunity.pk_id),
            1
          );
        }
      });
    }
  } else if (
    skuLevelOpportunities.find((opp) => opp.pk_id === changedOpportunity.pk_id)
  ) {
    const parentRow = state.bulkInputsMetadata.rows[parentIndex];
    const childRows = parentRow.subRows;
    const child = childRows[childIndex];
    if (isOpportunityChecked) {
      child.opportunities = newOpportunities
        .filter(
          (opportunityId) =>
            !changedOpportunity.disables_opportunity.includes(opportunityId)
        )
        .filter((value, index, self) => self.indexOf(value) === index);
    } else {
      child.opportunities = newOpportunities;
    }
    parentRow.opportunities = ppgLevelOpportunities
      .map((opportunity) => {
        const childrenWithParentOpportunityId = parentRow.subRows.filter(
          (childProduct) =>
            childProduct.opportunities.includes(opportunity.pk_id)
        );
        if (!childrenWithParentOpportunityId.length) {
          return null;
        }
        if (
          childrenWithParentOpportunityId.length === parentRow.subRows.length
        ) {
          return opportunity.pk_id;
        }
        return opportunity.intermediate_opp_id;
      })
      .filter((opportunityId) => opportunityId !== null)
      .filter((value, index, self) => self.indexOf(value) === index);
  }
}
